'use client';

import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import { TTS_EMOTIONS } from '@/app/constants';
import { buildEmotionRegex, PAUSE_REGEX, PAUSE_START_REGEX, TTS_CARET_ANCHOR, TTS_LIMITS } from './constant';

export type TtsTranslate = (key: string) => string;

export interface TtsTextToken {
  type: 'text' | 'pause' | 'emotion';
  text?: string;
  content?: TtsTextToken[];
  duration?: number;
  disabled?: boolean;
  emotionId?: string;
  emotionName?: string;
  tagBackgroundColor?: string;
  tagTextColor?: string;
  emotionTextBackgroundColor?: string;
}

const EMOTION_SYNTAX_REGEX = buildEmotionRegex();

export function getTtsEmotionMeta(emotionId: string, t: TtsTranslate, fallbackName?: string) {
  const emotion = TTS_EMOTIONS.find((item) => item.id === emotionId);
  if (!emotion) {
    return {
      emotionId,
      emotionName: fallbackName || emotionId,
      tagBackgroundColor: 'rgba(255, 107, 107, 0.24)',
      tagTextColor: '#FF6B6B',
      emotionTextBackgroundColor: 'rgba(255, 107, 107, 0.16)',
    };
  }

  return {
    emotionId,
    emotionName: t(emotion.labelKey),
    tagBackgroundColor: emotion.tagBackgroundColor,
    tagTextColor: emotion.tagTextColor,
    emotionTextBackgroundColor: emotion.emotionTextBackgroundColor,
  };
}

export function findTtsEmotionSyntax(text: string): RegExpExecArray | null {
  EMOTION_SYNTAX_REGEX.lastIndex = 0;
  return EMOTION_SYNTAX_REGEX.exec(text);
}

export function parseTtsRichText(value: string, t: TtsTranslate): TtsTextToken[][] {
  const safeValue = value || '';
  return safeValue.split('\n').map((line) => parseTtsRichTextLine(line, t));
}

function parseTtsRichTextLine(line: string, t: TtsTranslate): TtsTextToken[] {
  const tokens: TtsTextToken[] = [];
  let buffer = '';
  let cursor = 0;

  const flushBuffer = () => {
    if (!buffer) return;
    tokens.push({ type: 'text', text: buffer });
    buffer = '';
  };

  while (cursor < line.length) {
    const rest = line.slice(cursor);
    const legacyCombinedMatch = rest.match(/^\[([^:\]]+):([^\]]+)\]<emotion:\1>(.*?)<\/emotion>/);
    if (legacyCombinedMatch) {
      flushBuffer();
      const [rawMatch, emotionId, emotionName, text] = legacyCombinedMatch;
      const emotionMeta = getTtsEmotionMeta(emotionId, t, emotionName);
      tokens.push({
        type: 'emotion',
        ...emotionMeta,
        content: parseTtsRichTextLine(text, t),
      });
      cursor += rawMatch.length;
      continue;
    }

    const legacyMarkMatch = rest.match(/^<emotion:([^>]+)>(.*?)<\/emotion>/);
    if (legacyMarkMatch) {
      flushBuffer();
      const [rawMatch, emotionId, text] = legacyMarkMatch;
      const emotionMeta = getTtsEmotionMeta(emotionId, t);
      tokens.push({
        type: 'emotion',
        ...emotionMeta,
        content: parseTtsRichTextLine(text, t),
      });
      cursor += rawMatch.length;
      continue;
    }

    const emotionSyntaxMatch = findTtsEmotionSyntax(rest);
    if (emotionSyntaxMatch?.index === 0) {
      flushBuffer();
      const [rawMatch, emotionId, text] = emotionSyntaxMatch;
      const emotionMeta = getTtsEmotionMeta(emotionId, t);
      tokens.push({
        type: 'emotion',
        ...emotionMeta,
        content: parseTtsRichTextLine(text, t),
      });
      cursor += rawMatch.length;
      continue;
    }

    const legacyTagMatch = rest.match(/^\[([^:\]]+):([^\]]+)\]/);
    if (legacyTagMatch) {
      flushBuffer();
      const [rawMatch, emotionId, emotionName] = legacyTagMatch;
      const emotionMeta = getTtsEmotionMeta(emotionId, t, emotionName);
      tokens.push({
        type: 'emotion',
        ...emotionMeta,
        content: [],
      });
      cursor += rawMatch.length;
      continue;
    }

    const pauseMatch = rest.match(PAUSE_START_REGEX);
    if (pauseMatch) {
      flushBuffer();
      const [rawMatch, rawDuration] = pauseMatch;
      const duration = Number.parseFloat(rawDuration);
      tokens.push({
        type: 'pause',
        duration,
        disabled: duration < TTS_LIMITS.MIN_PAUSE || duration > TTS_LIMITS.MAX_PAUSE,
      });
      cursor += rawMatch.length;
      continue;
    }

    buffer += line[cursor];
    cursor += 1;
  }

  flushBuffer();
  return tokens;
}

interface InlineTextContent {
  type: 'text';
  text: string;
}

interface InlinePauseContent {
  type: 'pauseTag';
  attrs: {
    duration?: number;
    disabled?: boolean;
  };
}

interface InlineEmotionContent {
  type: 'emotionTag';
  attrs: {
    emotionId?: string;
    emotionName?: string;
    tagBackgroundColor?: string;
    tagTextColor?: string;
    emotionTextBackgroundColor?: string;
  };
  content: InlineContentItem[];
}

type InlineContentItem = InlineTextContent | InlinePauseContent | InlineEmotionContent;

function getCharacterLength(char: string): number {
  const code = char.charCodeAt(0);
  return code >= 0x4e00 && code <= 0x9fff ? 2 : 1;
}

/**
 * Get character count from editor (excluding emotion tags, including pause tags)
 * Chinese: 2 chars per character, Other: 1 char per character
 * Pause tags: count actual length (e.g., <#1#> = 5, <#0.5#> = 7)
 */
export function getTextLength(editor: Editor | null): number {
  if (!editor) return 0;

  let count = 0;
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'pauseTag') {
      count += getPauseTagLength(node.attrs.duration as number);
      return false;
    }

    if (node.type.name === 'emotionTag') {
      count += getFragmentTextLength(node.content);
      return false;
    }

    if (node.isText) {
      const text = stripCaretAnchors(node.text || '');
      for (const char of text) {
        count += getCharacterLength(char);
      }
    }

    return true;
  });

  return count;
}

export function getPauseTagLength(duration: number): number {
  return 4 + String(duration).length;
}

/**
 * Serialize editor content to the canonical plain-text syntax used by form value.
 */
export function serializeEditorContent(editor: Editor | null): string {
  if (!editor) return '';
  return serializeInlineFragment(editor.state.doc.content);
}

export function serializeSelectionContent(editor: Editor | null): string {
  if (!editor || editor.state.selection.empty) {
    return '';
  }

  return serializeSelectedRange(editor.state.doc, editor.state.selection.from, editor.state.selection.to);
}

export function hasEmotionEligibleSelectionContent(editor: Editor | null): boolean {
  const selectedContent = serializeSelectionContent(editor);
  if (!selectedContent) {
    return false;
  }

  const contentWithoutPauseTags = selectedContent.replace(PAUSE_REGEX, '').trim();
  return contentWithoutPauseTags.length > 0;
}

function serializeSelectedRange(node: ProseMirrorNode, from: number, to: number, startPos = 0): string {
  if (from >= to) {
    return '';
  }

  if (node.isText) {
    const text = node.text ?? '';
    const textStart = Math.max(0, from - startPos);
    const textEnd = Math.min(text.length, to - startPos);
    return stripCaretAnchors(text.slice(textStart, textEnd));
  }

  if (node.type.name === 'pauseTag') {
    const nodeEnd = startPos + node.nodeSize;
    if (from < nodeEnd && to > startPos) {
      return `<#${node.attrs.duration}#>`;
    }
    return '';
  }

  if (node.type.name === 'emotionTag') {
    const contentStart = startPos + 1;
    const contentEnd = startPos + node.nodeSize - 1;
    const innerFrom = Math.max(from, contentStart);
    const innerTo = Math.min(to, contentEnd);
    const content = serializeChildrenInRange(node, innerFrom, innerTo, contentStart);

    if (from <= contentStart && to >= contentEnd) {
      return `{${node.attrs.emotionId}}${content}{/${node.attrs.emotionId}}`;
    }

    return content;
  }

  return serializeChildrenInRange(node, from, to, startPos + (node.type.name === 'doc' ? 0 : 1));
}

function serializeChildrenInRange(node: ProseMirrorNode, from: number, to: number, contentStartPos: number): string {
  let result = '';
  let childPos = contentStartPos;

  node.forEach((child) => {
    const childStart = childPos;
    const childEnd = childStart + child.nodeSize;

    if (to <= childStart || from >= childEnd) {
      childPos = childEnd;
      return;
    }

    result += serializeSelectedRange(child, from, to, childStart);
    childPos = childEnd;
  });

  return result;
}

function serializeInlineFragment(fragment: Fragment): string {
  let result = '';

  fragment.forEach((node) => {
    if (node.type.name === 'emotionTag') {
      const emotionId = String(node.attrs.emotionId || '');
      const emotionText = serializeInlineFragment(node.content);
      result += `{${emotionId}}${emotionText}{/${emotionId}}`;
      return;
    }

    if (node.type.name === 'pauseTag') {
      result += `<#${node.attrs.duration}#>`;
      return;
    }

    if (node.isText) {
      result += stripCaretAnchors(node.text ?? '');
    }
  });

  return result;
}

export function createEditorContentFromValue(value: string, t: TtsTranslate) {
  const inlineContent = createInlineContent(flattenTtsTextTokens(parseTtsRichText(value, t)));

  return {
    type: 'doc',
    content: inlineContent,
  };
}

function flattenTtsTextTokens(lines: TtsTextToken[][]): TtsTextToken[] {
  return lines.flatMap((line, index) => {
    if (index === 0) {
      return line;
    }

    return [{ type: 'text', text: '\n' } satisfies TtsTextToken, ...line];
  });
}

export function createEmotionNodes(editor: Editor, emotionId: string, text: string, t: TtsTranslate) {
  if (!emotionId) {
    return editor.schema.text(`{${emotionId}}${text}{/${emotionId}}`);
  }

  const emotion = getTtsEmotionMeta(emotionId, t);

  return [
    editor.schema.nodes.emotionTag.create(
      {
        emotionId,
        emotionName: emotion.emotionName,
        tagBackgroundColor: emotion.tagBackgroundColor,
        tagTextColor: emotion.tagTextColor,
        emotionTextBackgroundColor: emotion.emotionTextBackgroundColor,
      },
      createInlineFragmentFromTokens(flattenTtsTextTokens(parseTtsRichText(text, t)), editor.schema),
    ),
    editor.schema.text(TTS_CARET_ANCHOR),
  ];
}

export function insertTextWithSyntax(editor: Editor, text: string, t: TtsTranslate) {
  const inlineContent = createInlineFragmentFromTokens(flattenTtsTextTokens(parseTtsRichText(text, t)), editor.schema);
  editor.commands.insertContent(inlineContent.toJSON());
}

export function truncateTtsTextByLength(text: string, maxLength: number): string {
  if (maxLength <= 0 || !text) {
    return '';
  }

  let remainingLength = maxLength;
  let cursor = 0;
  let result = '';
  const emotionStack: string[] = [];

  while (cursor < text.length) {
    const rest = text.slice(cursor);
    const emotionTagMatch = rest.match(/^\{(\/?)([^{}]+)\}/);

    if (emotionTagMatch) {
      const [rawTag, closingMark, emotionId] = emotionTagMatch;
      result += rawTag;
      cursor += rawTag.length;

      if (closingMark) {
        if (emotionStack.at(-1) === emotionId) {
          emotionStack.pop();
        }
      } else {
        emotionStack.push(emotionId);
      }

      continue;
    }

    const pauseMatch = rest.match(PAUSE_START_REGEX);
    if (pauseMatch) {
      const [rawPause] = pauseMatch;
      if (rawPause.length > remainingLength) {
        break;
      }

      result += rawPause;
      remainingLength -= rawPause.length;
      cursor += rawPause.length;
      continue;
    }

    const char = text[cursor];
    const charLength = getCharacterLength(char);
    if (charLength > remainingLength) {
      break;
    }

    result += char;
    remainingLength -= charLength;
    cursor += 1;
  }

  while (emotionStack.length > 0) {
    result += `{/${emotionStack.pop()}}`;
  }

  return result;
}

function stripCaretAnchors(text: string): string {
  return text.replaceAll(TTS_CARET_ANCHOR, '');
}

function getFragmentTextLength(fragment: Fragment): number {
  let count = 0;

  fragment.forEach((node) => {
    if (node.type.name === 'pauseTag') {
      count += getPauseTagLength(node.attrs.duration as number);
      return;
    }

    if (node.type.name === 'emotionTag') {
      count += getFragmentTextLength(node.content);
      return;
    }

    if (node.isText) {
      const text = stripCaretAnchors(node.text || '');
      for (const char of text) {
        count += getCharacterLength(char);
      }
    }
  });

  return count;
}

function createInlineContent(tokens: TtsTextToken[]): InlineContentItem[] {
  const content: InlineContentItem[] = [];

  tokens.forEach((token) => {
    if (token.type === 'text') {
      content.push({
        type: 'text',
        text: token.text || '',
      });
      return;
    }

    if (token.type === 'pause') {
      content.push({
        type: 'pauseTag',
        attrs: {
          duration: token.duration,
          disabled: token.disabled,
        },
      });
      content.push({
        type: 'text',
        text: TTS_CARET_ANCHOR,
      });
      return;
    }

    content.push({
      type: 'emotionTag',
      attrs: {
        emotionId: token.emotionId,
        emotionName: token.emotionName,
        tagBackgroundColor: token.tagBackgroundColor,
        tagTextColor: token.tagTextColor,
        emotionTextBackgroundColor: token.emotionTextBackgroundColor,
      },
      content: createInlineContent(token.content || []),
    });
    content.push({
      type: 'text',
      text: TTS_CARET_ANCHOR,
    });
  });

  return content;
}

function createInlineFragmentFromTokens(tokens: TtsTextToken[], schema: Editor['schema']): Fragment {
  return createInlineFragmentFromContent(createInlineContent(tokens), schema);
}

function createInlineFragmentFromContent(items: InlineContentItem[], schema: Editor['schema']): Fragment {
  return Fragment.fromArray(
    items.flatMap((item) => {
      if (item.type === 'text') {
        const text = String(item.text || '');
        return text ? [schema.text(text)] : [];
      }

      if (item.type === 'pauseTag') {
        return [schema.nodes.pauseTag.create(item.attrs)];
      }

      return [schema.nodes.emotionTag.create(item.attrs, createInlineFragmentFromContent(item.content, schema))];
    }),
  );
}

export function stripCaretAnchorsFromFragment(fragment: Fragment, schema: Editor['schema']): Fragment {
  const nodes: ProseMirrorNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === 'pauseTag') {
      nodes.push(node);
      return;
    }

    if (node.type.name === 'emotionTag') {
      nodes.push(node.copy(stripCaretAnchorsFromFragment(node.content, schema)));
      return;
    }

    if (!node.isText) {
      return;
    }

    const text = stripCaretAnchors(node.text || '');
    if (!text) return;
    nodes.push(schema.text(text, node.marks));
  });

  return Fragment.fromArray(nodes);
}

export function extractInlineSelectionContent(fragment: Fragment, schema: Editor['schema']): Fragment {
  const nodes: ProseMirrorNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === 'pauseTag') {
      nodes.push(node);
      return;
    }

    if (node.type.name === 'emotionTag') {
      nodes.push(node.copy(stripCaretAnchorsFromFragment(node.content, schema)));
      return;
    }

    if (node.isText) {
      const text = stripCaretAnchors(node.text || '');
      if (text) {
        nodes.push(schema.text(text, node.marks));
      }
    }
  });

  return Fragment.fromArray(nodes);
}

export function hasMeaningfulContent(fragment: Fragment): boolean {
  let hasContent = false;

  fragment.forEach((node) => {
    if (hasContent) return;

    if (node.type.name === 'pauseTag') {
      hasContent = true;
      return;
    }

    if (node.type.name === 'emotionTag') {
      hasContent = hasMeaningfulContent(node.content);
      return;
    }

    if (node.isText && stripCaretAnchors(node.text || '').length > 0) {
      hasContent = true;
    }
  });

  return hasContent;
}

export function getFirstEmptyEmotionTagRange(doc: Editor['state']['doc']) {
  let emptyEmotionTagFrom: number | null = null;
  let emptyEmotionTagTo: number | null = null;

  doc.descendants((node, pos) => {
    if (node.type.name === 'emotionTag' && !hasMeaningfulContent(node.content)) {
      emptyEmotionTagFrom = pos;
      emptyEmotionTagTo =
        doc.textBetween(pos + node.nodeSize, pos + node.nodeSize + 1, '', '') === TTS_CARET_ANCHOR
          ? pos + node.nodeSize + 1
          : pos + node.nodeSize;
      return false;
    }

    return true;
  });

  if (emptyEmotionTagFrom === null || emptyEmotionTagTo === null) {
    return null;
  }

  return {
    from: emptyEmotionTagFrom,
    to: emptyEmotionTagTo,
  };
}

export function removeFirstEmptyEmotionTag(
  editor: Editor,
  emptyEmotionTagRange = getFirstEmptyEmotionTagRange(editor.state.doc),
) {
  const { tr } = editor.state;
  if (!emptyEmotionTagRange) {
    return false;
  }

  tr.delete(emptyEmotionTagRange.from, emptyEmotionTagRange.to);
  editor.view.dispatch(tr);
  return true;
}
